import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'
import { X } from 'lucide-react'
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type { QuickBooksFpaCachePrepareResponse, QuickBooksGlSummaryResponse, ServerApiClient } from '../../../data/ServerApiClient'
import type { CfoStatementGlDetailCell, CfoStatementGlDetailRequest } from './CfoPagePrimitives'
import { formatCurrencyCompact } from './cfoFormat'
import {
  DEFAULT_GL_DETAIL_COLUMNS,
  GL_DETAIL_NUMERIC_COLUMNS,
  cacheManifestToPrepareResponse,
  findGlDetailCacheTable,
  glDetailColumnLabel,
  glDetailColumnsFromRows,
  materializeQuickBooksGlDetailTable,
  normalizeGlDetailRow,
  previewRowsAsObjects,
  trimGlStr,
} from '../quickBooksGlDetailShared'
import {
  buildCfoGlDetailRowsSql,
  buildCfoGlDetailTotalsSql,
  formatGlCell,
  parseCfoGlDetailTotals,
  selectVisibleGlColumns,
  type CfoGlDetailQueryState,
} from './cfoGlDetailQuery'
import { setPendingCfoGlDetailHandoff, type CfoGlDetailHandoff } from './cfoGlDetailHandoff'
import { useCfoBootstrap } from './useCfoBootstrap'
import { useCfoAnalysisOptional, type CfoAnalysisGlDetailTarget } from './analysis'

// CfoGlDetailModal — the bounded, scoped GL-detail modal shared across CFO
// pages (extracted from the Financials statement drilldown). It loads a small
// filtered slice of the GL — server compact summary first, local DuckDB cache
// as fallback — for one account/period scope, and can escalate to the full GL
// detail explorer with the scope handed off. `useCfoInlineGlDetail` packages it
// as the analysis modal's inline "Open GL detail" opener for any interior page.

const GL_DETAIL_MODAL_ROW_LIMIT = 200
const GL_DETAIL_MODAL_VISIBLE_COLUMNS = Array.from(new Set([...DEFAULT_GL_DETAIL_COLUMNS, 'debit', 'credit', 'amount']))

const sleep = (ms: number): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, ms))

const normalizeGlRowLabel = (label: string): string => String(label || '').trim().replace(/\s+/g, ' ')

type GlCellCoord = {
  rowIndex: number
  columnIndex: number
}

type GlCellSelection = {
  anchor: GlCellCoord
  focus: GlCellCoord
}

const copyTextToClipboard = async (text: string): Promise<void> => {
  if (!text) return
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }
  if (typeof document === 'undefined') return
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', 'true')
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  textarea.style.top = '0'
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  textarea.remove()
}

function cacheHasReadyGlDetail(cache: QuickBooksFpaCachePrepareResponse | null | undefined): boolean {
  if (!cache) return false
  if (cache.manifest?.gl_detail_ready || cache.manifest?.gl_detail_status === 'ready') return Boolean(findGlDetailCacheTable(cache))
  const table = findGlDetailCacheTable(cache)
  return Boolean(table && ((table.rows || []).length || table.parquet_url || (table.parquet_urls || []).length || table.artifact_uri || (table.artifact_uris || []).length))
}

async function loadPreparedGlDetailCache(
  appApi: ServerApiClient,
  connectionId: string,
  onStatus: (status: string) => void,
): Promise<QuickBooksFpaCachePrepareResponse> {
  const status = typeof appApi.getQuickBooksFpaCacheStatus === 'function'
    ? await appApi.getQuickBooksFpaCacheStatus(connectionId)
    : null
  if (status?.manifest) {
    const cache = cacheManifestToPrepareResponse(status.manifest)
    if (cacheHasReadyGlDetail(cache)) return cache
  }
  if (typeof appApi.prepareQuickBooksFpaCache !== 'function') {
    throw new Error('QuickBooks GL detail cache is not available in this session.')
  }
  onStatus('Preparing GL detail...')
  const prepared = await appApi.prepareQuickBooksFpaCache(connectionId, { includeGl: true, asyncDetail: true })
  if (cacheHasReadyGlDetail(prepared)) return prepared
  if (typeof appApi.getQuickBooksFpaCacheStatus !== 'function') {
    throw new Error('QuickBooks GL detail is still preparing. Refresh this page in a minute.')
  }
  for (let attempt = 0; attempt < 30; attempt += 1) {
    await sleep(attempt < 6 ? 1000 : 2000)
    const next = await appApi.getQuickBooksFpaCacheStatus(connectionId)
    const nextStatus = trimGlStr(next?.manifest?.gl_detail_status)
    onStatus(nextStatus === 'generating' ? 'Preparing GL detail...' : 'Checking GL detail...')
    if (next?.manifest?.gl_detail_status === 'failed') throw new Error('QuickBooks GL detail cache generation failed.')
    if (next?.manifest) {
      const cache = cacheManifestToPrepareResponse(next.manifest)
      if (cacheHasReadyGlDetail(cache)) return cache
    }
  }
  throw new Error('QuickBooks GL detail is still preparing. Refresh this page in a minute.')
}

function isGlDetailDisplayColumn(column: string): boolean {
  return !/(^id$|_id$)/i.test(column)
}

export function glDetailPeriodBounds(period: string | null | undefined): { startDate: string; endDate: string } {
  const match = /^(\d{4})-(\d{2})/.exec(String(period || '').trim())
  if (!match) return { startDate: '', endDate: '' }
  const year = Number(match[1])
  const month = Number(match[2])
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return { startDate: '', endDate: '' }
  const end = new Date(Date.UTC(year, month, 0))
  return {
    startDate: `${year}-${String(month).padStart(2, '0')}-01`,
    endDate: `${end.getUTCFullYear()}-${String(end.getUTCMonth() + 1).padStart(2, '0')}-${String(end.getUTCDate()).padStart(2, '0')}`,
  }
}

function parseGlDetailAccountLabel(label: string): { accountNumber: string; accountName: string } {
  const normalized = normalizeGlRowLabel(label).replace(/[›⌄]/g, '').trim()
  const match = /^([A-Za-z0-9.-]+)\s*[·-]\s*(.+)$/.exec(normalized)
  if (match) return { accountNumber: match[1].trim(), accountName: match[2].trim() }
  return { accountNumber: '', accountName: normalized }
}

function glDetailRequestCells(detail: CfoStatementGlDetailRequest): CfoStatementGlDetailCell[] {
  return Array.isArray(detail.selectedCells) && detail.selectedCells.length ? detail.selectedCells : [detail]
}

export function glDetailScope(
  detail: CfoStatementGlDetailCell,
  availableColumns: string[],
): Pick<CfoGlDetailQueryState, 'search' | 'startDate' | 'endDate' | 'filters'> {
  const bounds = glDetailPeriodBounds(detail.period)
  const parsedLabel = parseGlDetailAccountLabel(detail.rowLabel)
  const accountNumber = trimGlStr(detail.accountNumber || parsedLabel.accountNumber)
  const accountName = trimGlStr(detail.accountName || parsedLabel.accountName || detail.rowLabel)
  const filters: CfoGlDetailQueryState['filters'] = []
  if (detail.accountId && availableColumns.includes('account_id')) {
    filters.push({ column: 'account_id', mode: 'expression', values: [], operator: 'equals', expression: detail.accountId })
  } else if (accountName && availableColumns.includes('account_name')) {
    filters.push({ column: 'account_name', mode: 'expression', values: [], operator: 'contains', expression: accountName })
  } else if (accountName && availableColumns.includes('account')) {
    filters.push({ column: 'account', mode: 'expression', values: [], operator: 'contains', expression: accountName })
  } else if (accountNumber && availableColumns.includes('account_number')) {
    filters.push({ column: 'account_number', mode: 'expression', values: [], operator: 'equals', expression: accountNumber })
  }
  return {
    search: filters.length ? '' : accountName,
    startDate: bounds.startDate,
    endDate: bounds.endDate,
    filters,
  }
}

export function buildGlDetailQueryState(
  detail: CfoStatementGlDetailRequest,
  availableColumns: string[],
): CfoGlDetailQueryState {
  const cells = glDetailRequestCells(detail)
  const scopes = cells.map((cell) => glDetailScope(cell, availableColumns))
  const singleScope = scopes.length === 1 ? scopes[0] : null
  const dedupedScopes = Array.from(new Map(scopes.map((scope) => [
    JSON.stringify(scope),
    scope,
  ])).values())
  const visibleColumns = selectVisibleGlColumns(GL_DETAIL_MODAL_VISIBLE_COLUMNS, availableColumns)
  return {
    search: singleScope?.search || '',
    startDate: singleScope?.startDate || '',
    endDate: singleScope?.endDate || '',
    filters: singleScope?.filters || [],
    scopes: singleScope ? undefined : dedupedScopes,
    sortColumn: availableColumns.includes('txn_date') ? 'txn_date' : visibleColumns[0] || availableColumns[0] || '',
    sortDirection: 'desc',
    visibleColumns,
    page: 0,
    pageSize: GL_DETAIL_MODAL_ROW_LIMIT,
  }
}

// Columns assumed when handing a statement cell off to the full GL detail
// explorer: the canonical DuckDB GL table always materializes these, and
// account_id is deliberately excluded so the handed-off filter lands on a
// column the explorer's filter UI can display and let the user adjust.
const GL_DETAIL_HANDOFF_COLUMNS = ['txn_date', 'account_name', 'account', 'account_number']

export function buildGlDetailHandoff(detail: CfoStatementGlDetailRequest): CfoGlDetailHandoff {
  const state = buildGlDetailQueryState(detail, GL_DETAIL_HANDOFF_COLUMNS)
  return {
    search: state.search,
    startDate: state.startDate,
    endDate: state.endDate,
    filters: state.filters,
    scopes: state.scopes,
    label: detail.rowLabel,
  }
}

export type CfoGlDetailModalProps = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  connectionId: string
  duckDbOpts: Record<string, any> | null
  detail: CfoStatementGlDetailRequest
  onClose: () => void
  onOpenFullGlDetail?: (() => void) | null
}

export function CfoGlDetailModal({
  appApi,
  workerClient = null,
  workbookId,
  connectionId,
  duckDbOpts,
  detail,
  onClose,
  onOpenFullGlDetail = null,
}: CfoGlDetailModalProps) {
  const [loadStatus, setLoadStatus] = useState('Checking GL detail...')
  const [loadError, setLoadError] = useState('')
  const [rows, setRows] = useState<Record<string, any>[]>([])
  const [columns, setColumns] = useState<string[]>([])
  const [rowCount, setRowCount] = useState(0)
  const [totals, setTotals] = useState<Record<string, number>>({})
  const [cellSelection, setCellSelection] = useState<GlCellSelection | null>(null)
  const selectingCellsRef = useRef(false)
  const cellSelectionRef = useRef<GlCellSelection | null>(null)
  const requestCells = useMemo(() => glDetailRequestCells(detail), [detail])
  const periodBounds = glDetailPeriodBounds(detail.period)
  const valueLabel = requestCells.length > 1
    ? formatCurrencyCompact(requestCells.reduce((sum, cell) => sum + Number(cell.value || 0), 0))
    : detail.value == null ? '' : formatCurrencyCompact(detail.value)
  const numericTotalColumns = useMemo(
    () => columns.filter((column) => GL_DETAIL_NUMERIC_COLUMNS.has(column) && Object.prototype.hasOwnProperty.call(totals, column)),
    [columns, totals],
  )
  const selectionBounds = useMemo(() => {
    if (!cellSelection) return null
    return {
      minRowIndex: Math.min(cellSelection.anchor.rowIndex, cellSelection.focus.rowIndex),
      maxRowIndex: Math.max(cellSelection.anchor.rowIndex, cellSelection.focus.rowIndex),
      minColumnIndex: Math.min(cellSelection.anchor.columnIndex, cellSelection.focus.columnIndex),
      maxColumnIndex: Math.max(cellSelection.anchor.columnIndex, cellSelection.focus.columnIndex),
    }
  }, [cellSelection])
  const selectedCellSummary = useMemo(() => {
    if (!selectionBounds) return null
    let count = 0
    const numericTotals: Record<string, number> = {}
    for (let rowIndex = selectionBounds.minRowIndex; rowIndex <= selectionBounds.maxRowIndex; rowIndex += 1) {
      const row = rows[rowIndex]
      if (!row) continue
      for (let columnIndex = selectionBounds.minColumnIndex; columnIndex <= selectionBounds.maxColumnIndex; columnIndex += 1) {
        const column = columns[columnIndex]
        if (!column) continue
        count += 1
        if (GL_DETAIL_NUMERIC_COLUMNS.has(column)) {
          const value = Number(row[column] || 0)
          if (Number.isFinite(value)) numericTotals[column] = (numericTotals[column] || 0) + value
        }
      }
    }
    return { count, numericTotals }
  }, [columns, rows, selectionBounds])
  const selectedCellClipboardText = useMemo(() => {
    if (!selectionBounds) return ''
    const lines: string[] = []
    for (let rowIndex = selectionBounds.minRowIndex; rowIndex <= selectionBounds.maxRowIndex; rowIndex += 1) {
      const row = rows[rowIndex]
      if (!row) continue
      const values: string[] = []
      for (let columnIndex = selectionBounds.minColumnIndex; columnIndex <= selectionBounds.maxColumnIndex; columnIndex += 1) {
        const column = columns[columnIndex]
        values.push(column ? formatGlCell(row[column], column) : '')
      }
      lines.push(values.join('\t'))
    }
    return lines.join('\n')
  }, [columns, rows, selectionBounds])
  const modalTitle = requestCells.length > 1 ? `${requestCells.length} selected cells` : detail.rowLabel
  const modalSubtitle = requestCells.length > 1
    ? `${new Set(requestCells.map((cell) => cell.rowLabel)).size} rows / ${new Set(requestCells.map((cell) => cell.period || cell.columnLabel)).size} columns`
    : `${detail.columnLabel}${periodBounds.startDate ? ` / ${periodBounds.startDate} to ${periodBounds.endDate}` : ''}`

  useEffect(() => {
    // Window-level capture so Escape closes this modal first when it is
    // stacked over the analysis modal, whose document-capture Escape handler
    // would otherwise close the session underneath instead.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [onClose])

  useEffect(() => {
    const stopSelecting = () => {
      selectingCellsRef.current = false
    }
    window.addEventListener('mouseup', stopSelecting)
    return () => window.removeEventListener('mouseup', stopSelecting)
  }, [])

  useEffect(() => {
    const handleCopy = (event: ClipboardEvent) => {
      if (!selectedCellClipboardText || !selectedCellSummary) return
      event.clipboardData?.setData('text/plain', selectedCellClipboardText)
      event.preventDefault()
      setLoadStatus(`Copied ${selectedCellSummary.count} cell${selectedCellSummary.count === 1 ? '' : 's'}.`)
    }
    document.addEventListener('copy', handleCopy)
    return () => document.removeEventListener('copy', handleCopy)
  }, [selectedCellClipboardText, selectedCellSummary])

  useEffect(() => {
    let cancelled = false
    setRows([])
    setColumns([])
    setRowCount(0)
    setTotals({})
    setCellSelection(null)
    cellSelectionRef.current = null
    setLoadError('')
    const loadServerGlSummary = (appApi.getCfoGlSummary || appApi.getQuickBooksGlSummary)?.bind(appApi)
    const canUseServerGlSummary = workbookId && typeof loadServerGlSummary === 'function'
    if (canUseServerGlSummary) {
      const wb = trimGlStr(workbookId)
      if (!wb) {
        setLoadStatus('')
        setLoadError('GL summary is not available because this view is not bound to a workbook.')
        return () => {
          cancelled = true
        }
      }
      setLoadStatus('Loading compact GL summary...')
      loadServerGlSummary!(wb, {
          asOf: detail.period,
          statementType: detail.statementType,
          layoutNodeId: detail.nodeId,
          accountId: detail.accountId,
          limit: GL_DETAIL_MODAL_ROW_LIMIT,
        })
        .then((summary: QuickBooksGlSummaryResponse) => {
          if (cancelled) return
          // Compact server rows key columns by whichever raw GL export alias
          // matched (PascalCase `Date` arrives as `date`, never `txn_date`);
          // normalize to canonical ids so cells line up with the headers.
          const normalizedRows = summary.rows.map((row) => normalizeGlDetailRow(row))
          const allColumns = glDetailColumnsFromRows(normalizedRows)
          const visibleColumns = selectVisibleGlColumns(GL_DETAIL_MODAL_VISIBLE_COLUMNS, allColumns)
          setColumns(visibleColumns.length ? visibleColumns : allColumns.slice(0, 8))
          setRows(normalizedRows)
          setRowCount(summary.row_count)
          setTotals(summary.totals || {})
          setLoadStatus(summary.row_count ? `Loaded ${summary.row_count.toLocaleString()} server GL row${summary.row_count === 1 ? '' : 's'}.` : 'No GL rows match this statement cell.')
        })
        .catch((error: any) => {
          if (cancelled) return
          setLoadError(trimGlStr(error?.message || error) || 'Compact GL summary failed to load.')
          setLoadStatus('')
        })
      return () => {
        cancelled = true
      }
    }
    if (!connectionId || !workerClient || typeof workerClient.duckdb_run_sql !== 'function' || !duckDbOpts) {
      setLoadStatus('')
      setLoadError('GL detail is not available in this session.')
      return () => {
        cancelled = true
      }
    }
    setLoadStatus('Checking GL detail...')
    loadPreparedGlDetailCache(appApi, connectionId, setLoadStatus)
      .then(async (cache) => {
        if (cancelled) return
        setLoadStatus('Loading GL detail...')
        const loaded = await materializeQuickBooksGlDetailTable({
          workerClient,
          appApi,
          connectionId,
          cache,
          duckDbOpts,
        })
        if (cancelled) return
        const availableColumns = loaded.columns.filter(isGlDetailDisplayColumn)
        if (!availableColumns.length) throw new Error('GL detail columns are not available.')
        const queryState = buildGlDetailQueryState(detail, availableColumns)
        const visibleColumns = selectVisibleGlColumns(queryState.visibleColumns, availableColumns)
        const numericColumns = Array.from(GL_DETAIL_NUMERIC_COLUMNS).filter((column) => availableColumns.includes(column))
        setLoadStatus('Filtering GL detail...')
        const [totalsResult, rowsResult] = await Promise.all([
          workerClient.duckdb_run_sql(buildCfoGlDetailTotalsSql({
            state: queryState,
            availableColumns,
            numericColumns,
          }), {
            ...duckDbOpts,
            previewRowLimit: 1,
            timeoutMs: 120000,
            caller: 'QuickBooksCfoCore:financials_gl_detail_totals',
          }),
          workerClient.duckdb_run_sql(buildCfoGlDetailRowsSql({
            state: queryState,
            availableColumns,
            maxPageSize: GL_DETAIL_MODAL_ROW_LIMIT,
          }), {
            ...duckDbOpts,
            previewRowLimit: GL_DETAIL_MODAL_ROW_LIMIT,
            timeoutMs: 120000,
            caller: 'QuickBooksCfoCore:financials_gl_detail_rows',
          }),
        ])
        if (cancelled) return
        const totals = parseCfoGlDetailTotals(previewRowsAsObjects(totalsResult)[0] || {}, numericColumns)
        setColumns(visibleColumns)
        setRows(previewRowsAsObjects(rowsResult))
        setRowCount(totals.rowCount)
        setTotals(totals.totals)
        setLoadStatus(totals.rowCount ? `Loaded ${totals.rowCount.toLocaleString()} GL row${totals.rowCount === 1 ? '' : 's'}.` : 'No GL rows match this statement cell.')
      })
      .catch((error: any) => {
        if (cancelled) return
        setLoadError(trimGlStr(error?.message || error) || 'GL detail failed to load.')
        setLoadStatus('')
      })
    return () => {
      cancelled = true
    }
  }, [appApi, connectionId, detail, duckDbOpts, workerClient, workbookId])

  const startCellSelection = (rowIndex: number, columnIndex: number, event: ReactMouseEvent) => {
    if (event.button !== 0) return
    event.preventDefault()
    const focus = { rowIndex, columnIndex }
    const next = event.shiftKey && cellSelectionRef.current ? { anchor: cellSelectionRef.current.anchor, focus } : { anchor: focus, focus }
    selectingCellsRef.current = true
    cellSelectionRef.current = next
    setCellSelection(next)
  }

  const extendCellSelection = (rowIndex: number, columnIndex: number) => {
    if (!selectingCellsRef.current) return
    const focus = { rowIndex, columnIndex }
    const next = cellSelectionRef.current ? { anchor: cellSelectionRef.current.anchor, focus } : { anchor: focus, focus }
    cellSelectionRef.current = next
    setCellSelection(next)
  }

  const finishCellSelection = (rowIndex: number, columnIndex: number, event: ReactMouseEvent) => {
    if (event.button !== 0 || !selectingCellsRef.current) return
    const focus = { rowIndex, columnIndex }
    const next = cellSelectionRef.current ? { anchor: cellSelectionRef.current.anchor, focus } : { anchor: focus, focus }
    cellSelectionRef.current = next
    setCellSelection(next)
    selectingCellsRef.current = false
  }

  const isCellSelected = (rowIndex: number, columnIndex: number) =>
    Boolean(selectionBounds && rowIndex >= selectionBounds.minRowIndex && rowIndex <= selectionBounds.maxRowIndex && columnIndex >= selectionBounds.minColumnIndex && columnIndex <= selectionBounds.maxColumnIndex)

  const clearCellSelection = () => {
    cellSelectionRef.current = null
    setCellSelection(null)
  }

  const copySelectedCells = useCallback(async () => {
    if (!selectedCellClipboardText || !selectedCellSummary) return
    await copyTextToClipboard(selectedCellClipboardText)
    setLoadStatus(`Copied ${selectedCellSummary.count} cell${selectedCellSummary.count === 1 ? '' : 's'}.`)
  }, [selectedCellClipboardText, selectedCellSummary])

  const renderTotalsCell = (column: string, index: number) => {
    if (GL_DETAIL_NUMERIC_COLUMNS.has(column)) {
      return (
        <td key={column} className="num">
          {formatGlCell(totals[column] || 0, column)}
        </td>
      )
    }
    return (
      <td key={column} className={index === 0 ? 'cfo-gl-total-label' : undefined}>
        {index === 0 ? 'Total' : ''}
      </td>
    )
  }

  return (
    <div className="cfo-financials-gl-modal-backdrop" role="presentation" onMouseDown={() => onClose()}>
      <section
        className="cfo-financials-gl-modal"
        role="dialog"
        aria-modal="true"
        aria-label="GL detail"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="cfo-financials-gl-modal-head">
          <div>
            <span>GL detail</span>
            <h3>{modalTitle}</h3>
            <p>{modalSubtitle}</p>
          </div>
          <div className="cfo-financials-gl-modal-actions">
            {onOpenFullGlDetail ? (
              <button type="button" className="btn" onClick={onOpenFullGlDetail}>
                Open in GL detail
              </button>
            ) : null}
            <button type="button" className="btn icon" aria-label="Close GL detail" onClick={onClose}>
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        </header>
        <div className="cfo-financials-gl-modal-meta" aria-label="GL detail summary">
          <span>Rows {rowCount.toLocaleString()}</span>
          {valueLabel ? <span>{requestCells.length > 1 ? 'Selected' : 'Statement'} {valueLabel}</span> : null}
          {numericTotalColumns.map((column) => (
            <span key={column}>Total {glDetailColumnLabel(column)} {formatGlCell(totals[column] || 0, column)}</span>
          ))}
          {loadStatus ? <span>{loadStatus}</span> : null}
        </div>
        {loadError ? <div className="cfo-gl-state">{loadError}</div> : null}
        <div className="cfo-financials-gl-modal-table">
          <table className="cfo-gl-table">
            <thead>
              <tr>
                {columns.length ? columns.map((column) => (
                  <th key={column} className={GL_DETAIL_NUMERIC_COLUMNS.has(column) ? 'num' : undefined}>{glDetailColumnLabel(column)}</th>
                )) : <th>Detail</th>}
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((row, rowIndex) => (
                <tr key={`${row.txn_date || ''}-${row.num || ''}-${row.account_id || ''}-${rowIndex}`}>
                  {columns.map((column, columnIndex) => (
                    <td
                      key={column}
                      className={[
                        GL_DETAIL_NUMERIC_COLUMNS.has(column) ? 'num' : '',
                        isCellSelected(rowIndex, columnIndex) ? 'cfo-gl-cell-selected' : '',
                      ].filter(Boolean).join(' ') || undefined}
                      onMouseDown={(event) => startCellSelection(rowIndex, columnIndex, event)}
                      onMouseEnter={() => extendCellSelection(rowIndex, columnIndex)}
                      onMouseUp={(event) => finishCellSelection(rowIndex, columnIndex, event)}
                    >
                      {formatGlCell(row[column], column)}
                    </td>
                  ))}
                </tr>
              )) : (
                <tr>
                  <td colSpan={Math.max(1, columns.length)} className="cfo-gl-empty">
                    {loadError ? 'GL detail is unavailable.' : loadStatus || 'No GL rows match this statement cell.'}
                  </td>
                </tr>
              )}
            </tbody>
            {rowCount > 0 && numericTotalColumns.length && columns.length ? (
              <tfoot>
                <tr>
                  {columns.map((column, index) => renderTotalsCell(column, index))}
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
        {selectedCellSummary ? (
          <div className="cfo-statement-selection-summary cfo-gl-selection-summary">
            <span>{selectedCellSummary.count} cell{selectedCellSummary.count === 1 ? '' : 's'}</span>
            {Object.entries(selectedCellSummary.numericTotals).slice(0, 3).map(([column, value]) => (
              <b key={column}>{glDetailColumnLabel(column)} {formatGlCell(value, column)}</b>
            ))}
            <button type="button" className="btn" onClick={() => void copySelectedCells()}>Copy</button>
            <button type="button" className="btn" onClick={clearCellSelection}>Clear</button>
          </div>
        ) : null}
        {rows.length && rowCount > rows.length ? (
          <footer className="cfo-financials-gl-modal-footer">
            Showing first {rows.length.toLocaleString()} rows
          </footer>
        ) : null}
      </section>
    </div>
  )
}

/**
 * Map an analysis-modal GL target to a scoped modal request. The default is
 * deliberately strict: it only handles targets that carry a real `accountId`,
 * because composite metrics (net income, ratios) cannot scope a GL account
 * filter — returning null there falls back to the GL explorer navigation.
 */
export function resolveGlDetailTargetByAccount(
  target: CfoAnalysisGlDetailTarget,
  period?: string | null,
): CfoStatementGlDetailRequest | null {
  const accountId = trimGlStr(target.accountId)
  if (!accountId) return null
  const label = trimGlStr(target.metricLabel) || accountId
  const normalizedPeriod = trimGlStr(period)
  return {
    nodeId: trimGlStr(target.metricId) || accountId,
    rowLabel: label,
    statementType: null,
    accountId,
    accountNumber: null,
    accountName: label,
    columnKey: normalizedPeriod || 'all',
    columnLabel: normalizedPeriod || 'All available dates',
    period: normalizedPeriod || null,
    value: null,
  }
}

export type UseCfoInlineGlDetailOptions = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  duckDbOpts: Record<string, any> | null
  onNavSelect: (navId: string) => void
  /** Current page period (YYYY-MM-DD) used to month-scope the default resolver. */
  period?: string | null
  /** Override target resolution (e.g. Financials resolves statement rows). */
  resolveTarget?: (target: CfoAnalysisGlDetailTarget) => CfoStatementGlDetailRequest | null
}

/**
 * Inline GL-detail opener for any interior CFO page: returns `openGlDetail` to
 * pass to `CfoInteriorPage` (which registers it with the shared analysis
 * modal) and `glDetailModal`, the scoped modal to render in the page body.
 * Resolution is ref-backed so the registered callback stays identity-stable
 * inside snapshotted analysis sessions while reading current options.
 */
export function useCfoInlineGlDetail(options: UseCfoInlineGlDetailOptions): {
  openGlDetail: (target: CfoAnalysisGlDetailTarget) => boolean
  glDetailModal: ReactNode
} {
  const analysis = useCfoAnalysisOptional()
  const { bootstrap } = useCfoBootstrap(options.appApi, options.workbookId)
  const connectionId = trimGlStr(bootstrap?.connection_id)
  const [request, setRequest] = useState<CfoStatementGlDetailRequest | null>(null)
  const optionsRef = useRef(options)
  useEffect(() => {
    optionsRef.current = options
  }, [options])
  const openGlDetail = useCallback((target: CfoAnalysisGlDetailTarget): boolean => {
    const current = optionsRef.current
    const resolved = current.resolveTarget
      ? current.resolveTarget(target)
      : resolveGlDetailTargetByAccount(target, trimGlStr(target.period) || current.period)
    if (!resolved) return false
    setRequest(resolved)
    return true
  }, [])
  const closeModal = useCallback(() => setRequest(null), [])
  const openFullGlDetail = useCallback(() => {
    if (!request) return
    setPendingCfoGlDetailHandoff(buildGlDetailHandoff(request))
    setRequest(null)
    // The modal may be stacked over an analysis session; close it so the
    // full explorer isn't hidden underneath.
    analysis?.close()
    optionsRef.current.onNavSelect('gl_detail')
  }, [analysis, request])
  const glDetailModal = request ? (
    <CfoGlDetailModal
      appApi={options.appApi}
      workerClient={options.workerClient}
      workbookId={options.workbookId}
      connectionId={connectionId}
      duckDbOpts={options.duckDbOpts}
      detail={request}
      onClose={closeModal}
      onOpenFullGlDetail={openFullGlDetail}
    />
  ) : null
  return { openGlDetail, glDetailModal }
}
